/**
 * FirmaFuarEsleModal.jsx — "Firmayı Fuara Eşle" popup'ı (Personel Ekle akışının 3. katmanı)
 *
 * Kişi fuarda ancak fuara eşli bir firma adına çalışabilir. Bu popup iki yoldan birini kurar:
 *   · Stand   — firma bir standda (katılımcı / dekoratör / taşeron) çalışıyor
 *   · Taşeron — firma bir dekoratörün taşeronu, belirli bir standa bağlı değil; hizmet tipi (Zemin,
 *               Temizlik, Güvenlik…) isteğe bağlı sorulur ve taşeron bağlantısına yazılır
 * Kurallar ve uçlar: services/kisiFirmaEsleService.js
 *
 * Stand araması fuar geneli çalışır (backlog #134): salon ön koşul değil, yalnızca daraltmadır; yazılan metin
 * stand adı/kodu/id'si ya da standdaki herhangi bir firmanın (katılımcı, dekoratör, taşeron) adıyla eşleşir.
 * Salon da metin de yoksa sormuyoruz — fuarın alfabetik ilk 20 standı bir arama sonucu değil.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button, ButtonGroup, Spinner, Alert, Input, Label,
} from "reactstrap";
import { Search, Check } from "lucide-react";
import {
  getFuarSalonlari, searchStandlar, searchFuarDekoratorleri, firmayiStandaEsle, firmayiTaseronOlarakEsle,
  STAND_ROLLERI,
} from "../../services/kisiFirmaEsleService";
import { getTaseronTipleri } from "../../services/firmaTaseronService";
import { useTranslation } from "react-i18next";

const STAND_MIN_KARAKTER = 2;
const STAND_SATIRI_FIRMA_SAYISI = 3;

/** Sonuç satırının firma özeti: "Firma A (Katılımcı) · Firma B (Dekoratör) +1". */
function firmaOzeti(firmalar) {
  const rolAdi = (f) => f.firmaTypeName || STAND_ROLLERI.find((r) => r.id === f.firmaTypeId)?.label || "";
  const gorunen = firmalar.slice(0, STAND_SATIRI_FIRMA_SAYISI)
    .map((f) => (rolAdi(f) ? `${f.firmaName} (${rolAdi(f)})` : f.firmaName));
  const fazla = firmalar.length - gorunen.length;
  return gorunen.join(" · ") + (fazla > 0 ? ` +${fazla}` : "");
}

/**
 * Arama kutusu + tıklanabilir sonuç listesi.
 * `ara(q)` dizi ya da `{ items, hasMore }` döndürür. `minKarakter` kadar yazılmadıkça sorulmaz ve `bosMetin` görünür.
 * `yan` arama kutusunun yanına (örn. salon süzgeci) konur. `ara` değişince (süzgeç) arama yeniden yapılır.
 */
function AramaListesi({ placeholder, ara, satir, anahtar, secili, onSec, minKarakter = 0, bosMetin = null, yan = null }) {
  const { t: tx } = useTranslation("components");
  const [q, setQ] = useState("");
  const [sonuc, setSonuc] = useState({ q: null, ara: null, rows: [], hasMore: false, error: null });

  const bekliyor = q.trim().length < minKarakter;

  useEffect(() => {
    if (bekliyor) return undefined;
    let ignore = false;
    const t = setTimeout(() => {
      Promise.resolve(ara(q.trim()))
        .then((out) => {
          if (ignore) return;
          const rows = Array.isArray(out) ? out : (out?.items || []);
          setSonuc({ q, ara, rows, hasMore: !Array.isArray(out) && Boolean(out?.hasMore), error: null });
        })
        .catch((e) => { if (!ignore) setSonuc({ q, ara, rows: [], hasMore: false, error: e.message }); });
    }, 300);
    return () => { ignore = true; clearTimeout(t); };
  }, [q, ara, bekliyor]);

  const yukleniyor = !bekliyor && (sonuc.q !== q || sonuc.ara !== ara);

  return (
    <>
      <div className="d-flex gap-2">
        <div className="position-relative flex-grow-1">
          <Search size={14} className="position-absolute text-muted" style={{ left: 12, top: 12 }} />
          <Input className="ps-5" value={q} placeholder={placeholder} onChange={(e) => setQ(e.target.value)} />
        </div>
        {yan}
      </div>
      <div className="list-group mt-2" style={{ maxHeight: 240, overflowY: "auto" }}>
        {bekliyor && bosMetin && <div className="list-group-item small text-muted">{bosMetin}</div>}
        {yukleniyor && <div className="list-group-item text-center py-3"><Spinner size="sm" /></div>}
        {!bekliyor && !yukleniyor && sonuc.error && <div className="list-group-item small text-danger">{sonuc.error}</div>}
        {!bekliyor && !yukleniyor && !sonuc.error && sonuc.rows.length === 0 && (
          <div className="list-group-item small text-muted">{tx("firmaFuarEsle.sonucYok")}</div>
        )}
        {!bekliyor && !yukleniyor && sonuc.rows.map((r) => {
          const aktif = secili != null && anahtar(r) === anahtar(secili);
          return (
            <button
              type="button" key={anahtar(r)} onClick={() => onSec(r)}
              className={`list-group-item list-group-item-action d-flex align-items-center gap-2 text-start${aktif ? " active" : ""}`}
            >
              <span className="flex-grow-1">{satir(r)}</span>
              {aktif && <Check size={16} />}
            </button>
          );
        })}
      </div>
      {!bekliyor && !yukleniyor && sonuc.hasMore && (
        <div className="small text-muted mt-1">{tx("firmaFuarEsle.dahaFazlaSonucVarAramayi")}</div>
      )}
    </>
  );
}

export default function FirmaFuarEsleModal({ firma, fuarId, fuarLabel, yeniFirma = false, onClose, onDone }) {
  const { t: tx } = useTranslation("components");
  const [yol, setYol] = useState("stand"); // stand | taseron
  const [salonlar, setSalonlar] = useState([]);
  const [salonId, setSalonId] = useState(""); // "" = tüm salonlar
  const [stand, setStand] = useState(null);
  const [rol, setRol] = useState("");
  const [dekorator, setDekorator] = useState(null);
  const [tipler, setTipler] = useState([]);
  const [tipId, setTipId] = useState(""); // "" = belirtilmedi
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let ignore = false;
    getFuarSalonlari(fuarId)
      .then((list) => { if (!ignore) setSalonlar(list || []); })
      .catch((e) => { if (!ignore) setError(e.message); });
    return () => { ignore = true; };
  }, [fuarId]);

  useEffect(() => {
    let ignore = false;
    getTaseronTipleri()
      .then((list) => { if (!ignore) setTipler(list); })
      .catch(() => { if (!ignore) setTipler([]); });
    return () => { ignore = true; };
  }, []);

  // Arama fonksiyonları sabit kimlikli olmalı; yoksa AramaListesi her render'da yeniden arar.
  const standAra = useCallback(
    (q) => searchStandlar(fuarId, { q, salonId: salonId ? Number(salonId) : null }),
    [fuarId, salonId],
  );
  const dekoratorAra = useCallback((q) => searchFuarDekoratorleri(fuarId, q), [fuarId]);

  const hazir = yol === "stand" ? Boolean(stand && rol) : Boolean(dekorator);

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      if (yol === "stand") {
        await firmayiStandaEsle({ firmaId: firma.firmaId, fuarId, stand, firmaTypeId: Number(rol), yeniFirma });
      } else {
        if (dekorator.firmaId === firma.firmaId) throw new Error(tx("firmaFuarEsle.firmaKendiTaseronuOlamaz"));
        await firmayiTaseronOlarakEsle({
          firmaId: firma.firmaId, fuarId, dekoratorFirmaId: dekorator.firmaId,
          taseronFirmaTypeId: Number(tipId) || undefined,
        });
      }
      onDone?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen toggle={saving ? undefined : onClose} centered>
      <ModalHeader toggle={saving ? undefined : onClose}>{tx("firmaFuarEsle.firmayiFuaraEsle")}</ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <div className="fw-semibold">{firma.firmaName}</div>
          <div className="text-muted small">{fuarLabel}</div>
        </div>

        {error && <Alert fade={false} color="danger" className="py-2 small">{error}</Alert>}

        <ButtonGroup className="w-100 mb-3">
          <Button color="primary" outline={yol !== "stand"} onClick={() => setYol("stand")}>{tx("firmaFuarEsle.birStanddaCalisiyor")}</Button>
          <Button color="primary" outline={yol !== "taseron"} onClick={() => setYol("taseron")}>{tx("firmaFuarEsle.dekoratorunTaseronu")}</Button>
        </ButtonGroup>

        {yol === "stand" ? (
          <>
            <AramaListesi
              placeholder={tx("firmaFuarEsle.standKodVeyaFirmaAdi")}
              ara={standAra}
              anahtar={(s) => s.standId}
              secili={stand}
              onSec={setStand}
              minKarakter={salonId ? 0 : STAND_MIN_KARAKTER}
              bosMetin={tx("firmaFuarEsle.aramakIcinStandKodVeya")}
              yan={(
                <Input
                  type="select" value={salonId} className="w-auto flex-shrink-0" style={{ maxWidth: 150 }}
                  onChange={(e) => setSalonId(e.target.value)}
                >
                  <option value="">{tx("firmaFuarEsle.tumSalonlar")}</option>
                  {salonlar.map((s) => <option key={s.salonId} value={s.salonId}>{s.salonName}</option>)}
                </Input>
              )}
              satir={(s) => (
                <>
                  <span className="fw-medium">{s.standName}</span>
                  {s.salonName && !String(s.standName).startsWith(s.salonName) && (
                    <span className="small opacity-75 ms-2">{s.salonName}</span>
                  )}
                  {s.firmalar.length > 0 && (
                    <span className="small opacity-75 d-block">{firmaOzeti(s.firmalar)}</span>
                  )}
                </>
              )}
            />
            {stand && <div className="small mt-2"><span className="text-muted">{tx("firmaFuarEsle.seciliStand")}{" "}</span><strong>{stand.standName}</strong></div>}
            <Label className="small mb-1 mt-3">{tx("firmaFuarEsle.standdakiRolu")}</Label>
            <Input type="select" value={rol} onChange={(e) => setRol(e.target.value)}>
              <option value="">{tx("firmaFuarEsle.secin")}</option>
              {STAND_ROLLERI.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </Input>
          </>
        ) : (
          <>
            <AramaListesi
              placeholder={tx("firmaFuarEsle.fuardakiDekoratorFirma")}
              ara={dekoratorAra}
              anahtar={(f) => f.firmaId}
              secili={dekorator}
              onSec={setDekorator}
              satir={(f) => <span className="fw-medium">{f.firmaName}</span>}
            />
            <Label className="small mb-1 mt-3">{tx("firmaFuarEsle.hizmetTipi")}</Label>
            <Input type="select" value={tipId} onChange={(e) => setTipId(e.target.value)}>
              <option value="">{tx("firmaFuarEsle.belirtilmedi")}</option>
              {tipler.map((t) => <option key={t.firmaTypeId} value={String(t.firmaTypeId)}>{t.label}</option>)}
            </Input>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={saving}>{tx("firmaFuarEsle.vazgec")}</Button>
        <Button color="success" onClick={submit} disabled={!hazir || saving}>
          {saving ? <Spinner size="sm" /> : tx("firmaFuarEsle.esle")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
